/**
 * Evaluate an array of simple conditions against provided form data.
 *
 * Condition shape (array element):
 *   {
 *     field: string;               // key in formData
 *     operator: string;            // one of '=', '!=', '<', '<=', '>', '>=', 'in', 'not in', 'includes', 'startsWith', 'endsWith', 'empty', 'notEmpty'
 *     value: any;                  // value to compare with (unused for 'empty'/'notEmpty')
 *   }
 *
 * Semantics:
 * - Supports both flat arrays and nested arrays (groups)
 * - Flat array: Logic type controlled by `logic` parameter: 'AND' (default) or 'OR'
 * - Nested array: Each group internally uses AND logic, groups are OR-ed together
 *   Example: [[cond1, cond2], [cond3, cond4]] => (cond1 AND cond2) OR (cond3 AND cond4)
 * - Unknown operators cause that specific condition to fail (returns false) but are logged in dev mode.
 * - Numeric comparisons attempt a numeric coercion before comparison when both sides look numeric.
 * - 'in' / 'not in': value must be an array; compares using strict equality.
 * - 'includes': formValue must be an array or string; checks inclusion of value.
 * - 'empty' / 'notEmpty': checks for null/undefined/''/[] length.
 */

const evaluateSingleCondition = (formData, { field, operator = '=', value }) => {
  const formValue = formData ? formData[field] : undefined;

  // Helper checks
  const isEmpty = (v) => v === undefined || v === null || (typeof v === 'string' && v.trim() === '') || (Array.isArray(v) && v.length === 0);
  const looksNumeric = (v) => (typeof v === 'number') || (typeof v === 'string' && v !== '' && !isNaN(Number(v)));

  let left = formValue;
  let right = value;
  if (looksNumeric(left) && looksNumeric(right)) {
    left = Number(left);
    right = Number(right);
  }

  switch (operator) {
    case '=':
      return left === right;
    case '!=':
      return left !== right;
    case '<':
      return left < right;
    case '<=':
      return left <= right;
    case '>':
      return left > right;
    case '>=':
      return left >= right;
    case 'in':
      return Array.isArray(right) ? right.includes(left) : false;
    case 'not in':
      return Array.isArray(right) ? !right.includes(left) : false;
    case 'includes':
      if (Array.isArray(left)) return left.includes(right);
      if (typeof left === 'string') return left.includes(String(right));
      return false;
    case 'startsWith':
      return typeof left === 'string' && left.startsWith(String(right));
    case 'endsWith':
      return typeof left === 'string' && left.endsWith(String(right));
    case 'empty':
      return isEmpty(left);
    case 'notEmpty':
      return !isEmpty(left);
    default:
      if (process && process.env && process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(`[evaluateCondition] Unknown operator: ${operator}`);
      }
      return false; // unknown operator => fail condition
  }
};

export const evaluateCondition = (formData = {}, conditions = [], logic = 'AND') => {
  if (!Array.isArray(conditions) || conditions.length === 0) return true; // no conditions => pass

  // Check if this is a nested array (groups of conditions)
  // A nested array has arrays as its first element
  const isNested = Array.isArray(conditions[0]);

  if (isNested) {
    // Nested structure: each group is AND-ed internally, groups are OR-ed together
    // [[cond1, cond2], [cond3, cond4]] => (cond1 AND cond2) OR (cond3 AND cond4)
    return conditions.some(group => {
      if (!Array.isArray(group)) return false;
      return group.every(condition => evaluateSingleCondition(formData, condition));
    });
  } else {
    // Flat structure: use the logic parameter
    const evaluator = logic === 'OR' ? 'some' : 'every';
    return conditions[evaluator](condition => evaluateSingleCondition(formData, condition));
  }
};



